import React from "react";
import { Link } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import {
  Phone, UsersThree, Wallet, Briefcase, AirplaneTilt, VideoCamera,
  GlobeHemisphereWest, Sparkle, ShieldCheck,
} from "@phosphor-icons/react";

const CHIPS = [
  { icon: Phone, label: "Call.", color: "text-sky-400" },
  { icon: UsersThree, label: "Connect.", color: "text-emerald-400" },
  { icon: Wallet, label: "Pay.", color: "text-amber-400" },
  { icon: Briefcase, label: "Work.", color: "text-blue-400" },
  { icon: AirplaneTilt, label: "Travel.", color: "text-fuchsia-400" },
  { icon: VideoCamera, label: "Meet.", color: "text-teal-400" },
];

const FEATURES = [
  { icon: GlobeHemisphereWest, ring: "border-sky-500/40 text-sky-400", title: "CONNECT GLOBALLY", titleColor: "text-sky-400", sub: "Calls · Messaging · eSIMs · Global Numbers" },
  { icon: Briefcase, ring: "border-emerald-500/40 text-emerald-400", title: "POWER YOUR BUSINESS", titleColor: "text-emerald-400", sub: "Business PBX · iYeneh Assistant · Meetings" },
  { icon: Wallet, ring: "border-amber-500/40 text-amber-400", title: "ONE UNIVERSAL WALLET", titleColor: "text-amber-400", sub: "Fund once. Access services across iYeneh." },
  { icon: Sparkle, ring: "border-fuchsia-500/40 text-fuchsia-400", title: "ASK iYeneh", titleColor: "text-fuchsia-400", sub: "Your 24/7 SI to find, understand, use & buy what you need." },
];

export function AuthHero() {
  const brand = useBrand();
  return (
    <div data-testid="auth-hero" className="relative hidden overflow-hidden bg-[#05070f] p-10 md:order-first md:flex md:flex-col md:justify-between lg:p-12">
      <img src="/auth-hero-globe.jpg" alt="" aria-hidden="true"
        className="pointer-events-none absolute right-[-6%] top-1/2 w-[70%] max-w-none -translate-y-1/2 select-none opacity-80" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#05070f] via-[#05070f]/75 to-transparent" />
      <div className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-[#10B981]/10 blur-3xl" />

      <div className="relative z-10">
        <Link to="/" data-testid="auth-hero-logo-home" aria-label={`${brand.brand_name} — Home`}>
          <span className="animate-logo-in inline-flex items-center gap-2"><img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-12 w-12" /><span className="text-2xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span></span>
        </Link>
      </div>

      <div className="relative z-10 max-w-lg animate-fade-up">
        <h2 className="font-display text-3xl font-bold leading-[1.05] tracking-tight lg:text-[2.75rem]">
          ONE ACCOUNT. ONE WALLET.<br />
          A WORLD OF{" "}
          <span className="bg-gradient-to-r from-sky-400 via-emerald-400 to-amber-400 bg-clip-text text-transparent">POSSIBILITIES.</span>
        </h2>

        <div className="mt-4 h-px w-40 bg-gradient-to-r from-amber-500/70 to-transparent" />

        <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
          {CHIPS.map((c) => (
            <span key={c.label} className="flex items-center gap-1.5">
              <c.icon size={16} weight="fill" className={c.color} /> {c.label}
            </span>
          ))}
        </div>

        <p className="mt-5 text-sm text-white/70">Grow your business — all from one powerful platform.</p>
        <p className="text-sm text-white/70">Everything you need, connected through <span className="font-semibold text-amber-400">iYeneh.</span></p>

        <div className="mt-6 space-y-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="flex items-start gap-3">
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border ${f.ring} bg-white/[0.03]`}>
                <f.icon size={20} weight="duotone" />
              </div>
              <div>
                <p className={`text-sm font-bold tracking-wide ${f.titleColor}`}>{f.title}</p>
                <p className="text-[13px] leading-snug text-white/60">{f.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 space-y-3">
        <div className="flex items-center gap-2 rounded-2xl border border-sky-500/30 bg-white/[0.03] px-4 py-3 backdrop-blur-sm">
          <ShieldCheck size={20} weight="fill" className="text-sky-400" />
          <span className="text-sm font-bold tracking-wide">ONE ACCOUNT. ONE WALLET.{" "}
            <span className="bg-gradient-to-r from-sky-400 to-emerald-400 bg-clip-text text-transparent">EVERYTHING YOU NEED.</span>
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/45">
          <ShieldCheck size={14} className="text-[#10B981]" weight="fill" /> Secured with JWT · bcrypt · OTP
        </div>
      </div>
    </div>
  );
}
